import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../api/firebase/config';
import {
  SearchFieldConfig,
  SearchFieldInputType,
  SearchMatchMode,
  SearchSettingsFieldType,
  SearchSettingsType,
  searchFieldInputType,
  searchMatchMode,
} from '../types/searchCustomize';
import { PatternType } from '../types/pattern';
import { ItemChoiceType, ItemListType } from '../types/common';
import { isChoiceDataType } from '../constants/itemValidation';

/** item_list の type → 検索フォームの入力タイプに変換
 *
 * 選択肢項目（ラジオボタン・プルダウン）は設定済みの選択肢から選んで検索する（#3204）。
 * ただし選択肢が未設定の項目を choice にすると、選べるものが無いプルダウンを出すことになり
 * 検索自体ができなくなるため、text（フリーテキスト・あいまい検索）にフォールバックする。
 * この判定を入口で一本化することで、検索UI・検索方式・一覧表示の choice 判定を揃える。
 */
const toFieldInputType = (
  type?: string,
  choices?: ItemChoiceType[],
): SearchFieldConfig['inputType'] => {
  if (type === 'date') return searchFieldInputType.date;
  if (type === 'number') return searchFieldInputType.number;
  if (isChoiceDataType(type) && (choices?.length ?? 0) > 0) return searchFieldInputType.choice;
  return searchFieldInputType.text;
};

/** 入力タイプごとのデフォルト検索方式
 *  - date: Datepicker（完全一致）をデフォルトにする。あいまいに切り替えると TextField 入力になる
 *  - choice: 選択肢の出力値そのものと突き合わせるため完全一致で固定する。
 *    あいまい（部分一致）だと出力値 "1" が "10" にもヒットしてしまう
 */
const toDefaultMatchMode = (inputType: SearchFieldConfig['inputType']): SearchMatchMode =>
  inputType === searchFieldInputType.date || inputType === searchFieldInputType.choice
    ? searchMatchMode.exact
    : searchMatchMode.fuzzy;

/** item_list から OCR読取項目の SearchFieldConfig を生成 */
const buildOcrItemFields = (itemList: ItemListType[]): SearchFieldConfig[] => {
  const fields: SearchFieldConfig[] = [];

  itemList.forEach(item => {
    if (item.is_active === false) return;

    if (item.children && item.children.length > 0) {
      // テーブル型: 子項目を個別に追加
      item.children.forEach(child => {
        if (child.is_active === false) return;
        const inputType = toFieldInputType(child.type, child.choices);
        fields.push({
          fieldName: child.id,
          label: `${item.parentValue} > ${child.childValue}`,
          inputType,
          enabled: false,
          showInList: false,
          matchMode: toDefaultMatchMode(inputType),
          isOcrItem: true,
          parentId: item.id,
          outputFormat: child.outputFormat,
          choices: inputType === searchFieldInputType.choice ? child.choices : undefined,
        });
      });
    } else {
      // 単一値フィールド
      const inputType = toFieldInputType(item.type, item.choices);
      fields.push({
        fieldName: item.id,
        label: item.parentValue,
        inputType,
        enabled: false,
        showInList: false,
        matchMode: toDefaultMatchMode(inputType),
        isOcrItem: true,
        outputFormat: item.outputFormat,
        choices: inputType === searchFieldInputType.choice ? item.choices : undefined,
      });
    }
  });

  return fields;
};

/** SearchFieldConfig → Firestore保存形式に変換 */
const toFirestoreFields = (fields: SearchFieldConfig[]): SearchSettingsFieldType[] =>
  fields.map(f => ({
    field_name: f.fieldName,
    label: f.label,
    input_type: f.inputType,
    enabled: f.enabled,
    match_mode: f.matchMode,
    is_ocr_item: f.isOcrItem ?? false,
    parent_id: f.parentId ?? null,
    show_in_list: f.showInList ?? false,
  }));

/** Firestore保存形式 → SearchFieldConfig に変換 */
const fromFirestoreFields = (saved: SearchSettingsFieldType[]): SearchFieldConfig[] =>
  saved.map(f => ({
    fieldName: f.field_name,
    label: f.label,
    inputType: f.input_type as SearchFieldConfig['inputType'],
    enabled: f.enabled,
    matchMode: f.match_mode as SearchMatchMode,
    isOcrItem: f.is_ocr_item,
    parentId: f.parent_id ?? undefined,
    showInList: f.show_in_list ?? false,
  }));

/**
 * 検索カスタマイズ設定を管理するフック
 * Firestoreに保存された設定を読み込み、パターンのitem_listからOCR項目を動的生成する
 */
export const useSearchCustomize = (
  defaultFields: SearchFieldConfig[],
  pattern: PatternType | null,
): {
  fields: SearchFieldConfig[];
  updateField: (fieldName: string, updates: Partial<SearchFieldConfig>) => void;
  resetToDefault: () => void;
  isFieldEnabled: (fieldName: string) => boolean;
  getMatchMode: (fieldName: string) => SearchMatchMode;
  getFieldInputType: (fieldName: string) => SearchFieldInputType | undefined;
  saveToFirestore: (userId: string) => Promise<SearchSettingsType>;
} => {
  // OCR項目をitem_listから動的生成
  const ocrItemFields = useMemo(
    () => (pattern?.item_list ? buildOcrItemFields(pattern.item_list) : []),
    [pattern?.item_list],
  );

  // 全フィールド = 固定フィールド + OCR項目
  const allDefaultFields = useMemo(
    () => [...defaultFields, ...ocrItemFields],
    [defaultFields, ocrItemFields],
  );

  // Firestoreの保存設定を適用して初期状態を構築
  const buildInitialFields = useCallback((): SearchFieldConfig[] => {
    const savedSettings = pattern?.search_settings;
    if (!savedSettings?.fields?.length) return allDefaultFields;

    const savedFields = fromFirestoreFields(savedSettings.fields);

    return allDefaultFields.map(defaultField => {
      const saved = savedFields.find(f => f.fieldName === defaultField.fieldName);
      if (saved) {
        const showInList = saved.showInList ?? false;
        // 選択肢項目は完全一致で固定する（#3204）。
        // 選択肢化される前に text 型として保存された match_mode='fuzzy' が残っていても、
        // 部分一致だと出力値 "1" が "10" にヒットするため保存値を採用しない。
        const isChoiceField = defaultField.inputType === searchFieldInputType.choice;
        return {
          ...defaultField,
          // canToggleList フィールド（作成者列など）は検索と一覧表示を1トグルで連動させるため、
          // 過去に enabled=true で保存された設定でも showInList に揃えてデフォルト非表示を担保する
          enabled: defaultField.canToggleList ? showInList : saved.enabled,
          matchMode: isChoiceField ? defaultField.matchMode : saved.matchMode,
          showInList,
        };
      }
      return defaultField;
    });
  }, [allDefaultFields, pattern?.search_settings]);

  const [fields, setFields] = useState<SearchFieldConfig[]>(buildInitialFields);

  // 同一パターンに対しては初回のみ初期化する。
  // 保存時の setPattern や Firestore リスナー再発火による pattern 参照変化で
  // buildInitialFields が再生成されてもユーザーの編集中設定を上書きしない。
  const initializedPatternIdRef = useRef<string | null>(null);
  useEffect(() => {
    const pid = pattern?.patternId ?? null;
    if (!pid) return;
    if (initializedPatternIdRef.current === pid) return;
    setFields(buildInitialFields());
    initializedPatternIdRef.current = pid;
  }, [pattern?.patternId, buildInitialFields]);

  const updateField = useCallback(
    (fieldName: string, updates: Partial<SearchFieldConfig>): void => {
      setFields(prev =>
        prev.map(field => (field.fieldName === fieldName ? { ...field, ...updates } : field)),
      );
    },
    [],
  );

  const resetToDefault = useCallback((): void => {
    setFields(allDefaultFields);
  }, [allDefaultFields]);

  // fieldName → SearchFieldConfig の参照安定なマップ
  // useCallback は依存配列が変わるたびに新しい関数を生成するため、Map を中間に挟むことで
  // 同じ fields の場合に不要な再計算を抑える
  const fieldsByName = useMemo(() => {
    const map = new Map<string, SearchFieldConfig>();
    fields.forEach(f => map.set(f.fieldName, f));
    return map;
  }, [fields]);

  const isFieldEnabled = useCallback(
    (fieldName: string): boolean => fieldsByName.get(fieldName)?.enabled ?? false,
    [fieldsByName],
  );

  const getMatchMode = useCallback(
    (fieldName: string): SearchMatchMode =>
      fieldsByName.get(fieldName)?.matchMode ?? searchMatchMode.fuzzy,
    [fieldsByName],
  );

  const getFieldInputType = useCallback(
    (fieldName: string): SearchFieldInputType | undefined => fieldsByName.get(fieldName)?.inputType,
    [fieldsByName],
  );

  const saveToFirestore = useCallback(
    async (userId: string): Promise<SearchSettingsType> => {
      if (!pattern?.patternId) throw new Error('Pattern not found');

      const searchSettings: SearchSettingsType = {
        fields: toFirestoreFields(fields),
        updated_at: Timestamp.now(),
        updated_by: userId,
      };

      // patterns.updated_at は更新しない: 検索設定の保存はパターン本体の更新ではないため、
      // 他箇所（プレビュー時刻判定など）が誤検知しないよう search_settings 配下のみに記録する
      const patternRef = doc(db, 'patterns', pattern.patternId);
      await updateDoc(patternRef, {
        search_settings: searchSettings,
      });

      return searchSettings;
    },
    [pattern?.patternId, fields],
  );

  return {
    fields,
    updateField,
    resetToDefault,
    isFieldEnabled,
    getMatchMode,
    getFieldInputType,
    saveToFirestore,
  };
};
